/**
 * Slot-machine ease-out-cubic tween between two integers. Returns a
 * `cancel()` so the caller can abort if the component unmounts or the
 * target changes mid-tween. Mirrors `tweenNumber()` in the prototype's
 * reputation.js but with explicit cancellation for React useEffect
 * cleanup semantics.
 */
export function tweenNumber(
  el: HTMLElement,
  from: number,
  to: number,
  duration: number,
  format: (value: number) => string
): () => void {
  if (from === to) {
    el.textContent = format(to);
    return () => {};
  }
  let raf = 0;
  let cancelled = false;
  const start = performance.now();
  const ease = (t: number) => 1 - Math.pow(1 - t, 3);

  const step = (now: number) => {
    if (cancelled) return;
    const t = Math.min(1, (now - start) / duration);
    const value = from + (to - from) * ease(t);
    el.textContent = format(Math.round(value));
    if (t < 1) {
      raf = requestAnimationFrame(step);
    } else {
      el.textContent = format(to);
    }
  };
  raf = requestAnimationFrame(step);

  return () => {
    cancelled = true;
    if (raf) cancelAnimationFrame(raf);
  };
}

export function formatBalance(value: number): string {
  return Number(value).toLocaleString();
}

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Signed delta for the popover's top-earners rows — ported alongside `ReputationSourceRow` so
 * the two apps format a points change identically. The minus is U+2212, not a hyphen, so it
 * lines up with the plus at the same optical weight.
 */
export function formatSignedDelta(value: number): string {
  if (value > 0) return `+${formatBalance(value)}`;
  if (value < 0) return `−${formatBalance(Math.abs(value))}`;
  return '±0';
}
